import React, { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { LeftPanel } from './LeftPanel';
import { MapView } from './MapView';
import { useMillSelection } from '../../hooks/useMillSelection';
import { useWelcome } from '../../context/WelcomeContext';
import { Mill } from '../../lib/types';

export const ExplorerView: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { open: welcomeOpen } = useWelcome();
  const {
    allMills,
    selectedState,
    selectedMill,
    millsInState,
    selectState,
    selectMill,
  } = useMillSelection();

  // ?mode=scenario nudge — scrolls the scenarios section into view and
  // adds a brief pulse via the `scenario-pulse` class. Voluntary, not
  // forced: no modal opens, user can ignore it. Deferred until the
  // welcome splash closes — otherwise the cue plays under the splash on
  // first-visit and the user never sees it. The pulse passed down to
  // ScenarioSelector is also gated on welcomeOpen so the animation
  // class arrives in DOM at the same moment as the scroll.
  const nudgeScenarios = searchParams.get('mode') === 'scenario';
  const showNudge = nudgeScenarios && !welcomeOpen;
  useEffect(() => {
    if (!showNudge) return;
    const el = document.querySelector('[data-scenario-selector]');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [showNudge]);

  const handleMillClick = (mill: Mill) => {
    selectMill(mill);
    // Navigate to detail view
    navigate(`/detail/${mill.id}`, { state: { mill } });
  };

  // Dropdown selection should navigate the same way a marker click does —
  // a guided user could otherwise pick a mill from the list and never realize
  // the map marker has to be clicked separately. Null means the dropdown was
  // cleared (or a state change wiped the mill); just deselect, no navigation.
  const handleMillSelect = (mill: Mill | null) => {
    if (mill) {
      handleMillClick(mill);
    } else {
      selectMill(null);
    }
  };

  return (
    <div className="relative md:flex md:flex-row h-full">
      <LeftPanel
        selectedState={selectedState}
        selectedMill={selectedMill}
        millsInState={millsInState}
        onStateChange={selectState}
        onMillChange={handleMillSelect}
        highlightScenarios={showNudge}
      />
      <MapView
        mills={allMills}
        selectedState={selectedState}
        selectedMill={selectedMill}
        onMillNavigate={handleMillClick}
      />
    </div>
  );
};
